// components/MUIDatatable/PharmaciesWithWalletTable.js
import React from "react";
import CustomMUIDataTable from "components/MUIDatatable/CustomMUIDataTablev2";
import { dbGet } from "utils/DBFetchers";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";
import Tooltip from "@material-ui/core/Tooltip";
import ScienceIcon from '@mui/icons-material/Science';

const MAX_OTHERS_TO_SHOW = 5;
const SEP = " · ";

export default function PharmaciesWithWalletMuiDataTable({
    pageSize: initialPageSize = 25,
    title = "Farmacias con Billetera",
    endpointBase = "/wallet/internal/pharmacies",
    columnsOverride = null,
    onRowClick,
    distributorId = null,
}) {
    const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();
    const TestMarker = ({ isTest }) =>
        !isTest ? null : (
            <Tooltip title="Farmacia de prueba">
                <ScienceIcon
                    fontSize="small"
                    style={{ color: "#757575", marginLeft: 6, verticalAlign: "text-bottom" }}
                    aria-label="Farmacia de prueba"
                />
            </Tooltip>
        );

    const [tableData, setTableData] = React.useState([]);
    const [count, setCount] = React.useState(0);
    const [totalPages, setTotalPages] = React.useState(0);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(initialPageSize);
    const [isLoading, setIsLoading] = React.useState(true);
    const [isDownloading, setIsDownloading] = React.useState(false);

    const [sortColumn, setSortColumn] = React.useState(null);
    const [sortDirection, setSortDirection] = React.useState(null);

    const COLS = [
        {
            key: "rut",
            label: "RUT",
            orderBy: "rut",
            format: (raw, row) => {
                const rut = raw ?? "";
                const isTest = !!row?.is_test_pharmacy;
                return (
                    <span style={{
                        whiteSpace: "nowrap",
                        color: isTest ? "#333" : undefined,
                        fontWeight: isTest ? 500 : undefined
                    }}>
                        {rut}
                        <TestMarker isTest={isTest} />
                    </span>
                );
            },
            options: {
                sort: true,
                setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 140 } }),
                setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
            },
        },
        {
            key: "distributor_name",
            label: "Distribuidor",
            format: (raw) => raw ?? "",
            options: {
                sort: false,
                setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 140 } }),
                setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
            },
        },
        {
            key: "all_pharmacy_names_with_rut",
            label: "Farmacias",
            format: (raw, row) => {
                const list = Array.isArray(raw) ? raw : [];

                const all = row?.all_pharmacy_names_with_rut;

                const isTest = !!row?.is_test_pharmacy;
                if (all.length <= MAX_OTHERS_TO_SHOW) {
                    return <span style={{ color: isTest ? "#555" : undefined }}>{all.join(SEP)}</span>;
                }
                const shown = all.slice(0, MAX_OTHERS_TO_SHOW);
                const remaining = all.length - shown.length;
                return (
                    <span style={{ color: isTest ? "#555" : undefined }}>
                        {shown.join(SEP)}
                        {SEP}+{remaining} más…
                    </span>
                );


            },
            options: {
                sort: false,
                setCellHeaderProps: () => ({ style: { whiteSpace: "nowrap", minWidth: 320 } }),
                setCellProps: () => ({ style: { whiteSpace: "nowrap" } }),
            },
        },
        { key: "names_count", label: "CantNombres", hidden: true, get: (row) => (Array.isArray(row.all_pharmacy_names_with_rut) ? row.all_pharmacy_names_with_rut.length : 0) },
    ];

    const DEFAULT_COLUMNS = COLS.map(({ label, options, hidden }) => ({
        name: label,
        options: hidden ? { ...(options || {}), display: "excluded", filter: false } : options,
    }));

    const columns = columnsOverride ?? DEFAULT_COLUMNS;

    const ORDER_BY_MAP = React.useMemo(() => {
        const entries = COLS.filter((c) => !!c.orderBy).map((c) => [c.label, c.orderBy]);
        return Object.fromEntries(entries);
    }, []);

    const makeQueryParams = React.useCallback(
        (extraParams = {}) => {
            const params = new URLSearchParams();
            params.append("page", page + 1);
            params.append("page_size", rowsPerPage);
            if (sortColumn) {
                const ob = ORDER_BY_MAP[sortColumn] || "rut";
                params.append("order_by", ob);
                if (sortDirection) params.append("order_dir", sortDirection);
            }

            if (distributorId !== null && distributorId !== undefined && distributorId !== "") {
                params.append("distributor_id", String(distributorId));
            }

            Object.entries(extraParams).forEach(([k, v]) => {
                if (v !== undefined && v !== null) {
                    params.append(k, String(v));
                }
            });
            return params;
        },
        [page, rowsPerPage, sortColumn, sortDirection, ORDER_BY_MAP, distributorId]
    );

    const fillTable = React.useCallback((apiPayload) => {
        const {
            page: bePage,
            page_size,
            total_pages,
            total_items,
            pharmacies = [],
        } = apiPayload || {};

        const nextPage = (bePage ?? 1) - 1;
        setPage((prev) => (prev !== nextPage ? nextPage : prev));
        if (typeof page_size === "number") setRowsPerPage((prev) => (prev !== page_size ? page_size : prev));
        if (typeof total_pages === "number") setTotalPages((prev) => (prev !== total_pages ? total_pages : prev));
        if (typeof total_items === "number") setCount((prev) => (prev !== total_items ? total_items : prev));

        const rows = pharmacies.map((row) =>
            COLS.map((col) => {
                const raw = col.get ? col.get(row) : row[col.key];
                const formatted = col.format ? col.format(raw, row) : (raw ?? "");
                return formatted;
            })
        );

        setTableData(rows);
    }, []);

    const fetchedKeyRef = React.useRef("");
    const inFlightRef = React.useRef({ key: "", running: false });

    const fetchPage = React.useCallback(() => {
        const params = makeQueryParams();
        const key = params.toString();

        if (inFlightRef.current.running && inFlightRef.current.key === key) {
            return;
        }

        if (fetchedKeyRef.current === key && tableData.length > 0) {
            setIsLoading(false);
            return;
        }
        inFlightRef.current = { key, running: true };
        setIsLoading(true);

        dbGet(`${endpointBase}?${params}`)
            .then((data) => {
                fetchedKeyRef.current = key;
                fillTable(data);
            })
            .catch(() => snackbarOpen("error", "Error al cargar farmacias"))
            .finally(() => {
                inFlightRef.current = { key: "", running: false };
                setIsLoading(false);
            });
    }, [makeQueryParams, endpointBase, fillTable, snackbarOpen, tableData.length]);

    React.useEffect(() => {
        fetchPage();
    }, [fetchPage]);

    const changePage = (newPage) => setPage(newPage);
    const changeRowsPerPage = (newRows) => {
        setRowsPerPage(newRows);
        setPage(0);
    };

    const handleSortChange = (col, dir) => {
        setSortColumn(col);
        setSortDirection(dir);
    };

    const handleDownload = () => {
        if (isDownloading) return false;
        setIsDownloading(true);
        (async () => {
            try {
                const params = makeQueryParams({ get_file_url: true });
                const data = await dbGet(`${endpointBase}?${params.toString()}`);
                const fileUrl = data?.file_url;
                if (!fileUrl) {
                    snackbarOpen("warning", "No se recibió URL de descarga");

                    return;
                }
                const a = document.createElement("a");
                a.href = fileUrl;
                a.target = "_blank";
                a.rel = "noopener noreferrer";
                document.body.appendChild(a);
                requestAnimationFrame(() => {
                    a.click();
                    document.body.removeChild(a);
                });
            } catch (e) {
                snackbarOpen("error", "No se pudo iniciar la descarga");
            } finally {
                setIsDownloading(false);
            }
        })();
        // 👇 MUY IMPORTANTE: cancelar descarga por defecto de MUIDataTable
        return false;
    };

    return (
        <>
            <CustomizedSnackbars
                severity={snackbarStatus.severity}
                mssg={snackbarStatus.message}
                open={snackbarStatus.open}
                onClose={onCloseSnackbar}
            />
            <CustomMUIDataTable
                title={title}
                data={tableData}
                columns={columns}
                count={count}
                page={page}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                changePage={changePage}
                changeRowsPerPage={changeRowsPerPage}
                options={{
                    pagination: true,
                    isLoading: isLoading || isDownloading,
                    filter: false,
                    search: false,
                    onColumnSortChange: handleSortChange,
                    onDownload: handleDownload,
                    onRowClick: onRowClick
                        ? (rowData /* , rowMeta */) => onRowClick(rowData)
                        : undefined,
                }}
            />
        </>
    );
}
